import { motion } from 'framer-motion';
import {
  Heart, Crown, Diamond, Camera, HeartPulse, Gift,
  Baby, Aperture, Cake, Award, Home, Clapperboard,
  Image, Package, Building2, Car,
} from 'lucide-react';

/* Each item has a unique icon animation type */
const ITEMS = [
  { label: 'Weddings', icon: Heart, anim: 'pulse' },
  { label: 'Hamarlok Weddings', icon: Crown, anim: 'bounce' },
  { label: 'Engagement', icon: Diamond, anim: 'spin' },
  { label: 'Pre Weddings', icon: Camera, anim: 'shake' },
  { label: 'Maternity', icon: HeartPulse, anim: 'pulse' },
  { label: 'Baby Shower', icon: Gift, anim: 'wobble' },
  { label: 'New Born', icon: Baby, anim: 'float' },
  { label: 'Baby Shoot', icon: Aperture, anim: 'spin' },
  { label: 'Birthday', icon: Cake, anim: 'bounce' },
  { label: 'Naming Ceremony', icon: Award, anim: 'shake' },
  { label: 'House Warming', icon: Home, anim: 'float' },
  { label: 'Cinematic Film', icon: Clapperboard, anim: 'wobble' },
  { label: 'Portfolio Shoot', icon: Image, anim: 'shake' },
  { label: 'Product Shoot', icon: Package, anim: 'bounce' },
  { label: 'Corporate Events', icon: Building2, anim: 'float' },
  { label: 'Car/Bike Shoot', icon: Car, anim: 'spin' },
];

/* Unique hover animation variants per type */
const ICON_ANIMS = {
  pulse: { scale: [1, 1.25, 1, 1.2, 1], transition: { duration: 0.6 } },
  bounce: { y: [0, -10, 0, -6, 0], transition: { duration: 0.5 } },
  spin: { rotate: [0, 360], scale: [1, 1.1, 1], transition: { duration: 0.6 } },
  shake: { x: [0, -6, 6, -4, 4, 0], transition: { duration: 0.4 } },
  wobble: { rotate: [0, -12, 12, -8, 8, 0], transition: { duration: 0.5 } },
  float: { y: [0, -8, 0], scale: [1, 1.15, 1], transition: { duration: 0.6, ease: 'easeInOut' } },
};

const containerVariants = {
  hidden: {},
  visible: { transition: { staggerChildren: 0.06 } },
};

const cardVariants = {
  hidden: { opacity: 0, y: 40, scale: 0.9 },
  visible: {
    opacity: 1, y: 0, scale: 1,
    transition: { duration: 0.5, ease: 'easeOut' },
  },
};

export default function WhatWeDo() {
  return (
    <section data-testid="what-we-do-section" className="py-24 sm:py-32 bg-visyra-bg">
      <div className="max-w-7xl mx-auto px-6">
        {/* Heading */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8 }}
          className="text-center mb-16 sm:mb-20"
        >
          <p className="font-body text-xs uppercase tracking-[0.3em] text-gold mb-4">Our Expertise</p>
          <h2
            data-testid="what-we-do-heading"
            className="font-heading text-3xl sm:text-4xl lg:text-5xl tracking-tight text-visyra-text mb-5"
          >
            What We Do?
          </h2>
          <div className="section-divider mx-auto" />
        </motion.div>

        {/* Grid — 4 columns on all screen sizes */}
        <motion.div
          variants={containerVariants}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, margin: '-80px' }}
          className="grid grid-cols-4 gap-3 sm:gap-4 lg:gap-5"
        >
          {ITEMS.map((item) => {
            const Icon = item.icon;
            const hoverAnim = ICON_ANIMS[item.anim];
            return (
              <motion.div
                key={item.label}
                variants={cardVariants}
                whileHover={{ y: -8, transition: { duration: 0.3 } }}
                data-testid={`wwd-${item.label.toLowerCase().replace(/\s+/g, '-')}`}
                className="group relative bg-visyra-card border border-white/5 hover:border-gold/30 rounded-sm p-3 sm:p-6 lg:p-8 flex flex-col items-center text-center transition-colors duration-500 cursor-default"
              >
                {/* Icon with unique hover animation */}
                <motion.div
                  whileHover={hoverAnim}
                  className="w-10 h-10 sm:w-16 sm:h-16 lg:w-20 lg:h-20 flex items-center justify-center mb-2 sm:mb-4 lg:mb-5"
                >
                  <Icon
                    className="text-visyra-text group-hover:text-gold transition-colors duration-400"
                    size={28}
                    strokeWidth={1.2}
                  />
                </motion.div>

                {/* Label */}
                <p className="font-heading text-[10px] sm:text-sm lg:text-base text-visyra-text group-hover:text-gold transition-colors duration-400 tracking-tight leading-tight">
                  {item.label}
                </p>

                {/* Bottom accent line */}
                <div className="absolute bottom-0 left-0 w-0 h-[2px] bg-gold group-hover:w-full transition-all duration-700" />
              </motion.div>
            );
          })}
        </motion.div>
      </div>
    </section>
  );
}
